/**
 * Build the ACTIVE_CONTEXT block sent on every chat turn.
 *
 * Reads the live MARS-page state from URL + Zustand stores so the agent
 * can resolve "this fund / this window" without asking.
 */
import { useMarsLiveStore } from '@/features/mars/live/store';
import { usePortalChatStore } from './store';
import type { ActiveContext, ChatPage } from './types';

function pageFromPath(pathname: string): ChatPage | null {
  if (pathname.startsWith('/mars-live')) return 'mars-live';
  if (
    pathname.startsWith('/mars-forecasts') ||
    pathname.startsWith('/forecasts') ||
    pathname.startsWith('/expected-returns')
  ) return 'mars-forecasts';
  if (pathname.startsWith('/mars-portfolio')) return 'mars-portfolio';
  return null;
}

/**
 * Returns null when the user is NOT on a MARS page; the widget hides
 * itself in that case so we never send a request the backend would
 * reject.
 */
export function buildActiveContext(pathname: string): ActiveContext | null {
  const page = pageFromPath(pathname);
  if (!page) return null;

  // mars-live store carries the live filters (fund, vintage, assets,
  // timeframe). Forecasts / portfolio pages don't have a single store
  // — fund/vintage are URL-scoped there, so we surface what we can.
  const live = useMarsLiveStore.getState();
  // Fund comes from the chat panel's own toggle now; mars-live's
  // selectedVintage / selectedAssets / timeframe are still pulled
  // through so the agent knows roughly what the user is looking at.
  const chatFund = usePortalChatStore.getState().fund;

  return {
    page,
    fund: chatFund,
    vintage_date: live.selectedVintage ?? null,
    selected_assets: Array.from(live.selectedAssets ?? []),
    timeframe: live.timeframe ?? null,
  };
}

export { pageFromPath };
